import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft } from "lucide-react";
import {
  ApiError,
  type ConsentKind,
  type LifeStage,
  type OnboardingStatus,
  type OnboardingUpdate,
  type Sex,
} from "@ferrivia/api-contract";
import { useApi, useTracker } from "@ferrivia/auth-web";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useMarket, useTranslation } from "@ferrivia/i18n";
import { useScreenBackground } from "../hooks/useScreenBackground";
import { useStaticAppHeight } from "../hooks/useStaticAppHeight";
import { ProgressDots } from "../components/ProgressDots";
import { consentKindsFor } from "./consentItems";
import { stepsFor, type Step } from "./marketRules";
import { AgeStep } from "./steps/AgeStep";
import { ConsentsStep } from "./steps/ConsentsStep";
import { EmailStep } from "./steps/EmailStep";
import { IntroStep } from "./steps/IntroStep";
import { LifeStageStep } from "./steps/LifeStageStep";
import { LocationStep } from "./steps/LocationStep";
import { NameStep } from "./steps/NameStep";
import { SexStep } from "./steps/SexStep";
import s from "./OnboardingFlow.module.css";

// Steps a single tap answers advance on that tap, so their Continue button is
// hidden — this is the "minimal clicks" rule, and the only place it applies.
// Everything else needs a deliberate Continue because the answer is typed or
// multi-select.
const AUTO_ADVANCE: Step[] = ["sex"];

const MIN_AGE = 18;
const MAX_AGE = 120;

function ageFromBirthYear(birthYear: number | null): string {
  return birthYear === null ? "" : String(new Date().getUTCFullYear() - birthYear);
}

function parseAge(value: string): number | null {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) && n >= MIN_AGE && n <= MAX_AGE ? n : null;
}

export interface OnboardingFlowProps {
  initial: OnboardingStatus;
  onDone: () => void;
}

export function OnboardingFlow({ initial, onDone }: OnboardingFlowProps) {
  const api = useApi();
  const tracker = useTracker();
  const { t } = useTranslation("client");
  const market = useMarket();
  const us = market === "us";
  const consentKinds = consentKindsFor(market);
  // brand-subtle, not surface-default: the token has to name the colour of the
  // page's topmost row of pixels, and this wash's 0% stop — and its
  // background-color — are both brand-subtle (styles.css). Login is the same
  // pairing with --color-brand, and it is the one confirmed seamless on
  // device; naming white here is what left a white status-bar strip above a
  // cream first row. See useScreenBackground.
  useScreenBackground("onboarding", "--color-brand-subtle");
  // Nothing on this screen moves when the keyboard opens — the footer keeps
  // its position and the keyboard covers it. See useStaticAppHeight.
  useStaticAppHeight();

  const [sex, setSex] = useState<Sex | null>(initial.answers.sex);
  const [age, setAge] = useState(ageFromBirthYear(initial.answers.birthYear));
  const [ageTouched, setAgeTouched] = useState(false);
  const [lifeStages, setLifeStages] = useState<LifeStage[]>(initial.answers.lifeStages);
  const [noLifeStage, setNoLifeStage] = useState(
    initial.answers.lifeStagesAnswered && initial.answers.lifeStages.length === 0,
  );
  // Finland pre-selected: the overwhelming majority of clients are answering
  // this from Finland, so defaulting it turns a required step into a single
  // tap for almost everyone. Only applies to a fresh answer — a returning
  // client's actual saved country still wins.
  const [country, setCountry] = useState(initial.answers.country ?? (us ? "US" : "FI"));
  const [region, setRegion] = useState(initial.answers.region ?? "");
  const [givenName, setGivenName] = useState(initial.identity.givenName ?? "");
  const [familyName, setFamilyName] = useState(initial.identity.familyName ?? "");
  const [city, setCity] = useState(initial.answers.city ?? "");
  const [email, setEmail] = useState(initial.answers.email ?? "");
  const [emailError, setEmailError] = useState<string | undefined>();
  const [consents, setConsents] = useState<Record<string, boolean>>(initial.consents);
  const [consentError, setConsentError] = useState<string | undefined>();
  const [pending, setPending] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);

  const startedAt = useRef(Date.now());

  // FTN sign-in derives birth_year from the HETU before onboarding starts,
  // so those clients are never asked — fixed at mount, since the only way
  // birthYear becomes known afterwards is by answering this very step.
  const knowsBirthYear = useRef(initial.answers.birthYear !== null).current;

  // The male branch never sees the life-stage question, so the step list —
  // and with it the progress bar — is derived from the answer rather than
  // fixed. Recomputed on every render because answering "male" three steps
  // in has to shorten the flow immediately.
  const steps = useMemo<Step[]>(
    () => stepsFor(market, sex, knowsBirthYear),
    [market, sex, knowsBirthYear],
  );

  const [step, setStep] = useState<Step>(() => {
    // Resume where they left off, but only if there is a "where" — a client
    // who has answered nothing gets the intro, which is what carries the name
    // confirmation and the non-prod skip. `missing` alone can't tell the two
    // apart: it lists every question for a first-timer too.
    const a = initial.answers;
    // Excludes birthYear: FTN sign-in derives it from the HETU before the
    // client has answered anything, so its presence alone is no longer
    // evidence of having started the flow.
    // In the US the email is known from the OTP sign-in, and the eID name
    // does not exist, so a typed name is what marks a started flow there.
    const started = us
      ? a.sex !== null ||
        a.country !== null ||
        a.city !== null ||
        a.region != null ||
        a.lifeStagesAnswered ||
        initial.identity.givenName !== null
      : a.sex !== null ||
        a.country !== null ||
        a.city !== null ||
        a.email !== null ||
        a.lifeStagesAnswered;
    if (!started) return "intro";
    // `missing` is server-computed and ordered, so its head is the first
    // outstanding question — correct after a reinstall or on a second device
    // too, because the answers live server-side rather than in this browser.
    return initial.missing[0] ?? "intro";
  });

  const index = steps.indexOf(step);
  const questionIndex = index - 1;
  const questionCount = steps.length - 1;
  const isLast = index === steps.length - 1;

  useEffect(() => {
    tracker.track("onboarding.started", { resumed: step !== "intro" });
    // Once per mount, not once per step — the step-level event is below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    tracker.track("onboarding.step_viewed", { step });
  }, [step, tracker]);

  // Saving is per step and optimistic: the answer is already in local state,
  // so a failed PATCH must not block the client from moving on. The next
  // step's save — and `complete` at the end — carry the whole picture again,
  // because PATCH is a partial upsert rather than a delta.
  const save = useCallback(
    async (update: OnboardingUpdate, forStep: Step) => {
      try {
        await api.onboarding.update(update);
        setSaveFailed(false);
        return true;
      } catch (err) {
        if (err instanceof ApiError && err.code === "email_in_use") {
          setEmailError(
            t(
              "onboarding.email.inUse",
              "Tämä sähköposti on jo käytössä toisella tilillä. Kokeile toista tai ota yhteyttä meihin.",
            ),
          );
          tracker.track("onboarding.save_failed", { step: forStep, reason: err.code });
          return false;
        }
        if (err instanceof ApiError && err.code === "consent_document_unpublished") {
          setConsentError(
            t(
              "onboarding.consents.unavailable",
              "Hyväksyntöjä ei voi juuri nyt tallentaa. Yritä hetken kuluttua uudelleen.",
            ),
          );
          tracker.track("onboarding.save_failed", { step: forStep, reason: err.code });
          return false;
        }
        setSaveFailed(true);
        tracker.track("onboarding.save_failed", {
          step: forStep,
          reason: err instanceof ApiError ? err.code : "network",
        });
        return true;
      }
    },
    [api, tracker, t],
  );

  const updateForStep = useCallback(
    (forStep: Step, overrides: { sex?: Sex } = {}): OnboardingUpdate => {
      switch (forStep) {
        case "sex":
          return { sex: overrides.sex ?? sex ?? undefined };
        case "name":
          return { givenName: givenName.trim(), familyName: familyName.trim() };
        case "age": {
          const parsed = parseAge(age);
          return parsed === null ? {} : { age: parsed };
        }
        case "life_stage":
          return { lifeStages };
        case "location":
          return {
            country: country ?? undefined,
            ...(us ? (region ? { region } : {}) : { city: city.trim() || undefined }),
          };
        case "email":
          return { email: email.trim() || undefined };
        case "consents":
          return {
            consents: consentKinds.map((kind) => ({
              kind,
              granted: consents[kind] ?? false,
            })),
          };
        default:
          return {};
      }
    },
    [sex, age, lifeStages, country, city, region, email, consents, givenName, familyName, us, consentKinds],
  );

  const stepValid = useCallback(
    (forStep: Step): boolean => {
      switch (forStep) {
        case "name":
          return givenName.trim().length > 0 && familyName.trim().length > 0;
        case "age":
          return parseAge(age) !== null;
        case "location":
          return Boolean(country) && (us ? region.length > 0 : city.trim().length > 0);
        case "email":
          return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
        case "consents":
          return consentKinds.every((kind) => consents[kind]);
        case "life_stage":
          return lifeStages.length > 0 || noLifeStage;
        default:
          return true;
      }
    },
    [age, country, city, region, email, consents, givenName, familyName, us, consentKinds, lifeStages, noLifeStage],
  );

  const advance = useCallback(
    async (overrides: { sex?: Sex } = {}) => {
      if (pending) return;
      setPending(true);
      try {
        const ok = await save(updateForStep(step, overrides), step);
        if (!ok) return;

        tracker.track("onboarding.step_completed", { step });

        // Recomputed rather than read off `steps`: answering "male" on this
        // very tap changes what comes next, and the memo has not re-run yet.
        const nextSteps =
          overrides.sex === undefined ? steps : stepsFor(market, overrides.sex, knowsBirthYear);

        const nextIndex = nextSteps.indexOf(step) + 1;
        if (nextIndex < nextSteps.length) {
          setStep(nextSteps[nextIndex]);
          return;
        }

        await api.onboarding.complete();
        tracker.track("onboarding.completed", { duration_ms: Date.now() - startedAt.current });
        onDone();
      } finally {
        setPending(false);
      }
    },
    [pending, save, updateForStep, step, steps, market, knowsBirthYear, tracker, api, onDone],
  );

  function goBack() {
    const previous = steps[index - 1];
    if (!previous) return;
    tracker.track("onboarding.step_back", { from_step: step, to_step: previous });
    setStep(previous);
  }

  async function skip() {
    if (pending) return;
    setPending(true);
    try {
      await api.onboarding.complete();
      tracker.track("onboarding.skipped", {});
      onDone();
    } catch {
      setSaveFailed(true);
    } finally {
      setPending(false);
    }
  }

  function handleSexAnswer(next: Sex) {
    setSex(next);
    void advance({ sex: next });
  }

  const showContinue = !AUTO_ADVANCE.includes(step);
  const continueLabel = step === "intro"
    ? t("onboarding.intro.start", "Aloitetaan")
    : isLast
      ? t("onboarding.finish", "Siirry Ferriviaan")
      : t("onboarding.continue", "Jatka");

  return (
    <Screen className={s.root}>
      <div className={s.scroll}>
        <div className={s.header}>
          {index > 0 ? (
            <button type="button" className={s.back} onClick={goBack}>
              <ChevronLeft aria-hidden size={18} />
              <span>{t("onboarding.back", "Takaisin")}</span>
            </button>
          ) : null}
        </div>

        {questionIndex >= 0 ? (
          <ProgressDots
            total={questionCount}
            current={questionIndex}
            label={t("onboarding.progress", "Kysymys {{current}} / {{total}}", {
              current: questionIndex + 1,
              total: questionCount,
            })}
          />
        ) : null}

        {step === "intro" ? (
          <IntroStep givenName={initial.identity.givenName} />
        ) : null}
        {step === "name" ? (
          <NameStep
            givenName={givenName}
            familyName={familyName}
            onGivenNameChange={setGivenName}
            onFamilyNameChange={setFamilyName}
            onSubmit={() => {
              if (stepValid("name")) void advance();
            }}
          />
        ) : null}
        {step === "sex" ? <SexStep value={sex} onAnswer={handleSexAnswer} /> : null}
        {step === "age" ? (
          <AgeStep
            value={age}
            showError={ageTouched && age.length > 0 && parseAge(age) === null}
            onChange={(v) => {
              setAge(v);
              setAgeTouched(true);
            }}
            onSubmit={() => {
              if (stepValid("age")) void advance();
            }}
          />
        ) : null}
        {step === "life_stage" ? (
          <LifeStageStep value={lifeStages} onChange={setLifeStages} none={noLifeStage} onNoneChange={setNoLifeStage} />
        ) : null}
        {step === "location" ? (
          <LocationStep
            country={country}
            city={city}
            region={region}
            onRegionChange={setRegion}
            onCountryChange={setCountry}
            onCityChange={setCity}
            onSubmit={() => {
              if (stepValid("location")) void advance();
            }}
          />
        ) : null}
        {step === "email" ? (
          <EmailStep
            value={email}
            errorText={emailError}
            onChange={(v) => {
              setEmail(v);
              setEmailError(undefined);
            }}
            onSubmit={() => {
              if (stepValid("email")) void advance();
            }}
          />
        ) : null}
        {step === "consents" ? (
          <ConsentsStep
            value={consents}
            documents={initial.documents}
            kinds={consentKinds}
            errorText={consentError}
            onToggle={(kind: ConsentKind, granted) => {
              setConsents((prev) => ({ ...prev, [kind]: granted }));
              setConsentError(undefined);
            }}
          />
        ) : null}

        {saveFailed ? (
          <Text as="p" size="sm" className={s.saveFailed} role="status">
            {t("onboarding.saveFailed", "Vastauksen tallennus ei onnistunut. Yritämme uudelleen.")}
          </Text>
        ) : null}
      </div>

      <div className={s.footer}>
        {showContinue ? (
          <Button
            className={s.continue}
            disabled={pending || !stepValid(step)}
            onClick={() => void advance()}
          >
            {continueLabel}
          </Button>
        ) : null}
        {/* Non-prod only, and the server says so — not the build. A production
            API refuses this call, so a mis-built client cannot bypass it.
            Offered on every step rather than only the first: a dev client who
            resumes mid-flow is exactly the person who most needs a way out. */}
        {!initial.answersRequired ? (
          <>
            <Button variant="ghost" className={s.skip} disabled={pending} onClick={() => void skip()}>
              {t("onboarding.skip", "Ohita")}
            </Button>
            <Text as="p" size="xs" className={s.skipNote}>
              {t("onboarding.intro.skipNote", "Kehitysympäristö — voit ohittaa kysymykset.")}
            </Text>
          </>
        ) : null}
      </div>
    </Screen>
  );
}
